import styles from './Toggle.module.css'

interface ToggleProps {
  readonly checked: boolean
  readonly onChange: (next: boolean) => void
  /** What the switch controls. Required — a switch with no name says nothing. */
  readonly label: string
  readonly disabled?: boolean
}

/**
 * An on/off switch.
 *
 * `role="switch"` with `aria-checked`, not a styled checkbox and not a button
 * whose label flips between "On" and "Off". A relabelling button announces a
 * *different control appearing* rather than the same one changing state, which
 * is the same reasoning as the marker input — and a switch is the role that
 * carries "this takes effect immediately" rather than "this will be submitted".
 *
 * The whole control is the target, not just the 20px track: 44px is the minimum
 * anything on this app is allowed to be, and a switch is the easiest thing in a
 * settings list to miss with a thumb.
 */
export function Toggle({ checked, onChange, label, disabled = false }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={`${styles['toggle']} ${checked ? styles['on'] : ''}`}
      onClick={() => {
        onChange(!checked)
      }}
    >
      <span className={styles['track']} aria-hidden="true">
        <span className={styles['knob']} />
      </span>
    </button>
  )
}
